import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ReconcileConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
  /** When true, the impact copy frames this as a retry of a safeguard-aborted run. */
  isRetry?: boolean;
}

/** The literal phrase the operator must type to arm the destructive confirm. */
const CONFIRM_PHRASE = 'RECONCILE';

/**
 * BLU-550 / Phase 58-05 — net-new typed-confirm gate for a FULL reconcile.
 *
 * Distinct from `PushConfirmDialog` (a preview-of-zeroing `Dialog`): this is a
 * shadcn `AlertDialog` whose destructive action only enables once the operator
 * types `RECONCILE` (trimmed, case-sensitive). A full reconcile force-pushes ERP
 * quantities to the live Shopify store for ~all variants and cannot be undone
 * mid-run, so the friction is intentional.
 */
export function ReconcileConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  isRetry = false,
}: ReconcileConfirmDialogProps) {
  const { t } = useTranslation('inventory');
  const inputId = useId();
  const [typed, setTyped] = useState('');

  // Reset the typed phrase whenever the dialog re-opens — never leave it armed.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const armed = typed.trim() === CONFIRM_PHRASE;

  return (
    <AlertDialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-threadheads-purple" aria-hidden="true" />
            {isRetry
              ? t('stockSync.reconcile.confirm.retryTitle', 'Retry full reconcile?')
              : t('stockSync.reconcile.confirm.title', 'Run a full reconcile?')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              'stockSync.reconcile.confirm.description',
              'This rebuilds the variant map, detects drift, then force-pushes corrected quantities to your live Shopify store.',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Impact box — amber per DESIGN.md §1 (warning / attention). */}
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-800">
                {t('stockSync.reconcile.confirm.impactTitle', 'This writes directly to Shopify')}
              </p>
              <p className="text-xs text-amber-700">
                {t(
                  'stockSync.reconcile.confirm.impactBody',
                  'Force-push overwrites Shopify with PrintRun quantities for ~all variants. It runs for several minutes and cannot be undone mid-run.',
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={inputId} className="text-sm">
            {t('stockSync.reconcile.confirm.typeLabel', 'Type RECONCILE to confirm')}
          </Label>
          <Input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder={CONFIRM_PHRASE}
            disabled={isPending}
            aria-describedby={`${inputId}-hint`}
            className="max-w-prose font-mono"
          />
          <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
            {t('stockSync.reconcile.confirm.typeHint', 'This guards against accidental full pushes.')}
          </p>
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('stockSync.reconcile.confirm.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!armed || isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {t('stockSync.reconcile.confirm.starting', 'Starting…')}
              </>
            ) : (
              t('stockSync.reconcile.confirm.run', 'Reconcile & push')
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
